'use client'

import { formatCurrencyWhole } from '@repo/utils/numbers'
import { styled } from '@mui/material/styles'
import { useClientProfileStore } from '@repo/store/useClientProfileStore'
import { useClientStore } from '@repo/store/useClientStore'
import { useEffect, useState } from 'react'
import MUIBox from '@mui/material/Box'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './ClientProfileCard.module.scss'

export const ClientProfileCard = ({ sx, testData }: ClientProfileCardProps) => {
  const { currentClient } = useClientStore()
  const { clientProfile, fetchClientProfile } = useClientProfileStore()
  const { clientNo } = currentClient || {}
  const [maxVisibleTags, setMaxVisibleTags] = useState<number>(4)

  useEffect(() => {
    clientNo && fetchClientProfile({ clientNo })
  }, [clientNo])

  useEffect(() => {
    const updateWidth = () => {
      setMaxVisibleTags(window.innerWidth < 1200 ? 2 : 4)
    }

    updateWidth() // Set initial width
    window.addEventListener('resize', updateWidth)

    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const uniqueTags = testData
    ? Array.from(new Set(testData?.workTypes?.map((tag: any) => tag.description) || []))
        .map((description) => {
          return testData?.workTypes?.find((tag: any) => tag.description === description)
        })
        .filter(Boolean)
    : Array.from(new Set(clientProfile?.workTypes?.map((tag: any) => tag.description) || []))
        .map((description) => {
          return clientProfile?.workTypes?.find((tag: any) => tag.description === description)
        })
        .filter(Boolean)

  const visibleTags = uniqueTags.slice(0, maxVisibleTags)
  const remainingTags = uniqueTags.slice(maxVisibleTags)

  const getRemainingTagsList = () => {
    return remainingTags.map((tag) => tag.description).join('\n')
  }

  const PillTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MUITooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#051C33',
      borderRadius: '4px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#fff',
      fontSize: '0.875rem',
      lineHeight: '1.4',
      maxWidth: '200px',
      padding: '0.5rem 0.75rem 0.35rem',
      left: '0',
      bottom: '10px',
      position: 'relative',
      [`&:before`]: {
        content: '""',
        width: '0',
        height: '0',
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderBottom: '5px solid #051C33',
        position: 'absolute',
        top: '-4px',
        left: '50%'
      }
    }
  }))

  return (
    <MUIPaper
      sx={{
        width: '100%',
        height: 'auto',
        margin: '0',
        background: 'white',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid #D8DCE5',
        position: 'relative',
        padding: '0',
        ...sx
      }}
      data-testid="ClientProfileCard"
    >
      <MUITypography
        sx={{
          padding: '1rem 1rem 0.75rem',
          marginBottom: '0'
        }}
        variant="h6"
      >
        Client profile
      </MUITypography>
      <MUIBox
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <MUIStack
          display="flex"
          direction="row"
          sx={{ margin: '0 1rem', width: 'calc(100% - 2rem)', flexWrap: 'wrap', rowGap: '0.5rem' }}
        >
          {visibleTags.map((tag: any) => (
            <MUITypography
              key={tag.workTypeId}
              variant="body2"
              className={styles.pods}
              sx={{
                backgroundColor: '#ECEFF1',
                border: '1px solid #ECEFF1',
                borderRadius: '16px',
                height: '2rem',
                textTransform: 'capitalize',
                textAlign: 'center',
                width: 'fit-content',
                padding: '6px 12px',
                margin: '0 0.25rem',
                whiteSpace: 'nowrap'
              }}
            >
              {tag.description}
            </MUITypography>
          ))}
          {remainingTags.length > 0 && (
            <PillTooltip disableFocusListener placement="bottom" title={getRemainingTagsList()} sx={{ mr: 4 }}>
              <MUITypography
                role="text"
                variant="body2"
                className={styles.pods}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: '#FFF',
                  border: '1px solid #3D517B',
                  borderRadius: '16px',
                  textTransform: 'capitalize',
                  textAlign: 'center',
                  width: 'fit-content',
                  padding: '6px 12px',
                  margin: '0 0.5rem 0.5rem 0',
                  whiteSpace: 'nowrap'
                }}
              >
                + {remainingTags.length} more
              </MUITypography>
            </PillTooltip>
          )}
        </MUIStack>
        <MUIStack
          display="flex"
          direction="row"
          gap={
            testData?.costsBilledCurrentYear === null || clientProfile.costsBilledCurrentYear === null ? '32px' : '8px'
          }
          sx={{ margin: '2rem', justifyContent: 'space-evenly', alignItems: 'center' }}
        >
          <MUIStack
            sx={{
              backgroundColor: '#FFF',
              borderRadius: '50%',
              border: '2px solid #ECEFF1',
              height: '7.5rem',
              width: '7.5rem',
              minHeight: '7.5rem',
              minWidth: '7.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <MUITypography variant="body1" sx={{ textTransform: 'uppercase', fontSize: '12px', color: '#3D517B' }}>
              Total matters
            </MUITypography>
            <MUITypography variant="body1" sx={{ fontSize: '20px', color: '#3D517B' }}>
              {testData ? testData.matterCount : clientProfile.matterCount}
            </MUITypography>
          </MUIStack>
          <MUIStack sx={{ height: '5rem', border: '1px solid #ECEFF1' }}></MUIStack>
          <MUIStack
            sx={{
              backgroundColor: '#ECEFF1',
              borderRadius: '50%',
              border: '2px solid #ECEFF1',
              height: '7.5rem',
              width: '7.5rem',
              minHeight: '7.5rem',
              minWidth: '7.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <MUITypography variant="body1" sx={{ textTransform: 'uppercase', fontSize: '12px', color: '#3D517B' }}>
              Lifetime spend
            </MUITypography>
            <MUITypography variant="body1" sx={{ fontSize: '20px', color: '#3D517B' }}>
              £
              {testData
                ? formatCurrencyWhole(testData.costsBilledTotal)
                : formatCurrencyWhole(clientProfile.costsBilledTotal)}
            </MUITypography>
          </MUIStack>
          <MUIStack
            sx={{
              backgroundColor: '#ECEFF1',
              borderRadius: '50%',
              border: '2px solid #ECEFF1',
              height: '7.5rem',
              width: '7.5rem',
              minHeight: '7.5rem',
              minWidth: '7.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <MUITypography variant="body1" sx={{ textTransform: 'uppercase', fontSize: '12px', color: '#3D517B' }}>
              Last FY
            </MUITypography>
            <MUITypography variant="body1" sx={{ fontSize: '20px', color: '#3D517B' }}>
              £
              {testData
                ? formatCurrencyWhole(testData.costsBilledLastYear)
                : formatCurrencyWhole(clientProfile.costsBilledLastYear)}
            </MUITypography>
          </MUIStack>
          {!testData?.costsBilledCurrentYear || !clientProfile.costsBilledCurrentYear ? (
            <MUIStack
              sx={{
                backgroundColor: '#ECEFF1',
                borderRadius: '50%',
                border: '2px solid #ECEFF1',
                height: '7.5rem',
                width: '7.5rem',
                minHeight: '7.5rem',
                minWidth: '7.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <MUITypography variant="body1" sx={{ textTransform: 'uppercase', fontSize: '12px', color: '#3D517B' }}>
                Current FY
              </MUITypography>
              <MUITypography variant="body1" sx={{ fontSize: '20px', color: '#3D517B' }}>
                £
                {testData
                  ? formatCurrencyWhole(testData.costsBilledCurrentYear)
                  : formatCurrencyWhole(clientProfile.costsBilledCurrentYear)}
              </MUITypography>
            </MUIStack>
          ) : null}
        </MUIStack>
      </MUIBox>
    </MUIPaper>
  )
}

ClientProfileCard.propTypes = {
  sx: PropTypes.object,
  testData: PropTypes.shape({
    workTypes: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string
      })
    ),
    matterCount: PropTypes.number,
    costsBilledTotal: PropTypes.number,
    costsBilledLastYear: PropTypes.number,
    costsBilledCurrentYear: PropTypes.number
  })
}

export type ClientProfileCardProps = {
  sx: object
  testData?: {
    workTypes: [{ description: string }]
    matterCount: number
    costsBilledTotal: number
    costsBilledLastYear: number
    costsBilledCurrentYear: number
  }
}
